import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { GOOGLE_CLIENT_ID, isGoogleAuthEnabled } from '../../config/google';
import styles from './GoogleSignInButton.module.css';

/**
 * Botón oficial de Google Identity Services.
 *
 * Se dibuja el botón de Google y no uno propio con el logo pegado porque las condiciones
 * de la marca lo exigen, y porque ese botón es el que abre el popup con la sesión que la
 * persona ya tiene en el navegador. Lo único que devuelve es el `credential`: un ID token
 * (JWT firmado por Google) que aquí no se abre ni se cree — lo valida el backend.
 */

// La librería de GIS no trae tipos; se declara lo mínimo que se usa.
interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'small' | 'medium' | 'large';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      width?: number;
      locale?: string;
    },
  ) => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/**
 * Una sola carga del script por pestaña, compartida entre login y registro: si cada
 * montaje insertara su propio <script>, el segundo se resolvería antes de que el primero
 * definiera window.google y renderButton fallaría en silencio.
 */
let gsiScriptPromise: Promise<void> | null = null;

const loadGsiScript = (): Promise<void> => {
  if (window.google?.accounts?.id) return Promise.resolve();

  if (!gsiScriptPromise) {
    gsiScriptPromise = new Promise<void>((resolve, reject) => {
      // La etiqueta anterior se BORRA en vez de reutilizarse. Reasignarle el mismo `src`
      // a un <script> que ya se ejecutó (o que ya falló) no vuelve a pedirlo: no dispara
      // load ni error, la promesa nunca se resuelve, onUnavailable nunca se llama y la
      // pantalla se queda con el separador "o" sobre un hueco vacío para siempre.
      document
        .querySelectorAll<HTMLScriptElement>(`script[src="${GSI_SRC}"]`)
        .forEach((stale) => stale.remove());

      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        // Se limpia la promesa para que un reintento (cambiar de pantalla y volver)
        // arranque de cero con una etiqueta nueva.
        gsiScriptPromise = null;
        script.remove();
        reject(new Error('No se pudo cargar Google Identity Services'));
      });
      document.head.appendChild(script);
    });
  }

  return gsiScriptPromise;
};

interface GoogleSignInButtonProps {
  /** Recibe el ID token de Google. */
  onCredential: (idToken: string) => void;
  /** Texto del botón: "Iniciar sesión con" vs "Registrarse con". */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  /** Se avisa hacia arriba si el script no cargó, para poder ocultar el separador. */
  onUnavailable?: () => void;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onCredential,
  text = 'continue_with',
  onUnavailable,
}) => {
  const holderRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const resolvedTheme = useAppSelector((state) => state.theme.resolvedTheme);

  // El callback vive en una ref: GIS guarda la función que recibió en initialize(), así
  // que si se re-inicializara en cada render para refrescarla, el botón parpadearía.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  const measure = useCallback(() => {
    const parent = holderRef.current?.parentElement;
    if (!parent) return;
    // GIS pide el ancho en pixeles y tope 400; sin esto el botón sale angosto y
    // desalineado respecto al botón primario de la tarjeta.
    setWidth(Math.min(400, Math.round(parent.getBoundingClientRect().width)));
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    if (!isGoogleAuthEnabled() || width === 0) return;

    let cancelled = false;

    loadGsiScript()
      .then(() => {
        if (cancelled || !holderRef.current || !window.google) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            if (response.credential) {
              onCredentialRef.current(response.credential);
            }
          },
          ux_mode: 'popup',
          // Sin selección automática: en el piso se comparten dispositivos y entrar
          // como la última cuenta usada sin preguntar es justo lo que no se quiere.
          auto_select: false,
        });

        holderRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(holderRef.current, {
          type: 'standard',
          theme: resolvedTheme === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
          width,
          locale: 'es',
        });
      })
      .catch(() => {
        if (!cancelled) onUnavailable?.();
      });

    return () => {
      cancelled = true;
    };
    // onUnavailable se omite a propósito: es un callback de los padres y volver a
    // ejecutar este efecto por su identidad redibujaría el botón en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, resolvedTheme, text]);

  if (!isGoogleAuthEnabled()) return null;

  return <div ref={holderRef} className={styles.holder} />;
};

export default GoogleSignInButton;
